import { zodResolver } from "@hookform/resolvers/zod";
import useIsMobile from "@hooks/useIsMobile";
import { WORK_EMAIL_REGEX } from "@utils/common";
import { insertAiChatbotDataInDB } from "database/supabase/freeTools";
import { ComponentType, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import ReactModal from "react-modal";
import { z } from "zod";

const Modal = ReactModal as unknown as ComponentType<ReactModal.Props>;

const desktopStyles: ReactModal.Styles = {
    content: {
        width: "600px",
        height: "390px",
        overflow: "hidden",
        padding: "0px",
        margin: "auto",
        top: "0px",
    },
    overlay: {
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        zIndex: 1000,
    },
};

const mobileStyles: ReactModal.Styles = {
    content: {
        width: "330px",
        height: "400px",
        overflow: "hidden",
        padding: "0px",
        margin: " auto",
        left: "15px",
        right: "15px",
        top: "0px",
    },
    overlay: {
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        zIndex: 1000,
    },
};

const AiChatbotSchema = z.object({
    name: z.string({ required_error: "First name is required" }).min(1, "Name is required"),
    businessEmail: z
        .string({ required_error: "Business email is required" })
        .min(1, "Business email is required")
        .refine((val) => WORK_EMAIL_REGEX.test(val), { message: "Invalid business email address" }),
});

type AiChatbotFormData = z.infer<typeof AiChatbotSchema>;

const AiChatbotLeadForm: React.FC = () => {
    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

    const isMobile = useIsMobile(768);

    useEffect(() => {
        const hasSubmitted = sessionStorage.getItem("aiChatbotLeadSubmitted");
        if (!hasSubmitted) {
            setModalIsOpen(true);
        }
    }, []);

    const closeModal = () => {
        setModalIsOpen(false);
    };
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<AiChatbotFormData>({
        resolver: zodResolver(AiChatbotSchema),
    });

    const onSubmit: SubmitHandler<AiChatbotFormData> = (formData) => {
        insertAiChatbotDataInDB(formData);
        sessionStorage.setItem("aiChatbotLeadSubmitted", "true");
        reset();
        setModalIsOpen(false);
    };

    return (
        <Modal isOpen={modalIsOpen} style={isMobile ? mobileStyles : desktopStyles} onRequestClose={closeModal} shouldCloseOnOverlayClick={false}>
            <section className="max-w-7xl mx-auto bg-gray-50 rounded shadow-2xl p-10">
                <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-3">
                    <div className="flex flex-col w-full mx-2">
                        <label htmlFor="name" className="mb-2 text-sm font-medium text-gray-700">
                            NAME<span className="px-1 text-xl text-red-900">*</span>
                        </label>
                        <input {...register("name")} type="text" placeholder="Name" className="px-4 py-1 border-black rounded-3xl " autoFocus />
                        <p className="text-red-500 text-base font-medium h-5 mb-2">{errors.name?.message}</p>

                        <label htmlFor="businessEmail" className="mb-2 text-sm font-medium text-gray-700">
                            BUSINESS EMAIL<span className="px-1 text-xl text-red-900">*</span>
                        </label>
                        <input {...register("businessEmail")} type="email" placeholder="Business email" className="px-4 py-1 border-black rounded-3xl " />
                        <p className="text-red-500 text-base font-medium h-5 mb-1">{errors.businessEmail?.message}</p>
                    </div>
                    <div className="md:col-span-2 flex justify-center">
                        <button type="submit" className="bg-blue-600 text-white px-10 py-4 rounded hover:bg-blue-700 transition">
                            Submit
                        </button>
                    </div>
                </form>
            </section>
        </Modal>
    );
};

export default AiChatbotLeadForm;
